/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl, defineMessages } from 'react-intl';
import merge from 'lodash/merge';
import isNil from 'lodash/isNil';
import { ComposedModal as SharedModal } from 'carbon-addons-iot-react';

import { handleClearError, determineDialogError } from './modalUtils';

const messages = defineMessages({
  save: 'Save',
  cancel: 'Cancel',
  close: 'Close modal',
});

type BaseModalType = {
  // TODO: make an error type
  error?: string | Record<any, any>;
  dataError?: string;
  /** callback function to trigger when the primary button is clicked */
  onSubmit?: () => void;
  header?: {
    helpText?: React.ReactNode;
    /** labels the top of the dialog */
    label?: string;
    /** large title within the dialog */
    title?: string;
  };
  footer?:
    | {
        /** optional property to hide the primary button of the modal; defaults to false */
        isPrimaryButtonHidden?: boolean;
        /** optional property to disabled the primary button of the modal; defaults to false */
        isPrimaryButtonDisabled?: boolean;
        primaryButtonText?: string;
        secondaryButtonText?: string;
      }
    | any;
  className?: string;
  isOpen: boolean;
  isLarge?: boolean;
  onClose: () => void;
  sendingData?: boolean | string; // either its a string or a bool
  isFetchingData?: boolean;
  clearSubmitErrors?: () => void;
  onClearDialogErrors?: () => void;
  type?: 'warn' | 'normal';
  children?: React.ReactNode;
  invalid?: boolean; // eslint-disable-line react/boolean-prop-naming
  submitFailed?: boolean; // eslint-disable-line react/boolean-prop-naming
  size?: string;
  /** Id that can be used for testing */
  testId?: string;
};

const defaultProps = {
  error: null,
  dataError: null,
  onSubmit: undefined,
  footer: null,
  header: null,
  className: null,
  isLarge: false,
  sendingData: false,
  clearSubmitErrors: null,
  onClearDialogErrors: null,
  type: null,
  children: null,
  invalid: false,
  submitFailed: false,
  isFetchingData: false,
  size: null,
  testId: 'BaseModal',
};

/**
 * Just exists to translate our modal strings and errors and map our prop model to the common one
 */
const BaseModal = ({
  error,
  dataError,
  footer,
  className,
  clearSubmitErrors,
  onClearDialogErrors,
  isOpen,
  isLarge,
  onClose,
  onSubmit,
  type,
  header,
  sendingData,
  isFetchingData,
  children,
  invalid,
  submitFailed,
  size,
  testId,
}: BaseModalType): JSX.Element => {
  const { formatMessage } = useIntl();

  const updatedFooter =
    !React.isValidElement(footer) && typeof footer === 'object' && !isNil(footer)
      ? merge({}, footer, {
          primaryButtonLabel: footer.primaryButtonText || formatMessage(messages.save),
          secondaryButtonLabel: footer.secondaryButtonText || formatMessage(messages.cancel),
        })
      : footer;

  return (
    <SharedModal
      testId={testId}
      size={size}
      open={isOpen}
      isLarge={isLarge}
      onClose={onClose}
      header={header}
      type={type}
      sendingData={!!sendingData}
      isFetchingData={!!isFetchingData}
      passiveModal={!footer && !onSubmit}
      submitFailed={submitFailed}
      className={classnames('base-modal', className)}
      iconDescription={formatMessage(messages.close)}
      error={determineDialogError(dataError, error, formatMessage)}
      onClearError={(): void => handleClearError(clearSubmitErrors, onClearDialogErrors)}
      footer={updatedFooter}
      invalid={invalid}
      onSubmit={onSubmit}>
      {children}
    </SharedModal>
  );
};

BaseModal.defaultProps = defaultProps;

export default BaseModal;
